// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { dia } from '@joint/core';
import { sampleGraphs } from '../config/sample-graphs';
import { convertGraphToJson, importGraphJsonFile } from '../utils/import-export';

// Constants
export const DEBOUNCE_DELAY = 100;
export const DEFAULT_EMPTY_GRAPH = { graph: { transport: [] } };
export const DEFAULT_HEIGHT = '500px';

// Utility functions
export const processDefaultGraph = () => {
  return typeof sampleGraphs.emergencyProcedure === 'string'
    ? JSON.parse(sampleGraphs.emergencyProcedure)
    : sampleGraphs.emergencyProcedure;
};

const ensureGraphInstance = (data) => {
  if (typeof data?.toJSON === 'function') return data;
  const graph = new dia.Graph();
  graph.fromJSON(data);
  return graph;
};

export const processStringGraph = (value, isReset, resetValues) => {
  try {
    const parsedData = isReset ? JSON.parse(resetValues.interactiveMap) : JSON.parse(value);

    const importedData = importGraphJsonFile(parsedData);
    return {
      cells: importedData.cells,
      tables: importedData.tables,
      modules: importedData.modules,
    };
  } catch (error) {
    console.error('Error processing graph data:', error);
    return processDefaultGraph();
  }
};

// Custom hook for graph initialization
export const useGraphInitialization = (parameterValue) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) return;
    setIsInitialized(true);
  }, [isInitialized]);
};

// Custom hook for graph changes
export const useGraphChangeHandler = (editMode, parameterValue, setParameterValue) => {
  const debounceTimeout = useRef(null);
  const hasInteracted = useRef(false);
  const handleGraphChange = useCallback(
    (newGraphData) => {
      if (!editMode) return;
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        try {
          // Mark that user has interacted with the map
          hasInteracted.current = true;

          const graphInstance = ensureGraphInstance(newGraphData);
          const convertedGraph = convertGraphToJson(graphInstance);

          if (parameterValue) {
            const currentValue = typeof parameterValue === 'string' ? JSON.parse(parameterValue) : parameterValue;

            if (currentValue.tables && !convertedGraph.tables) {
              convertedGraph.tables = currentValue.tables;
            }
          }
          setParameterValue(JSON.stringify(convertedGraph));
        } catch (error) {
          console.error('Error handling graph change:', error);
        }
      }, DEBOUNCE_DELAY);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editMode]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return handleGraphChange;
};

// Custom hook for processing initial graph
export const useInitialGraph = (parameterValue, scenarioResetValues) => {
  return useMemo(() => {
    try {
      // If there's a parameter value, use it
      if (parameterValue) {
        const isResetGraph = parameterValue === scenarioResetValues.interactiveMap;
        if (typeof parameterValue === 'string') {
          return processStringGraph(parameterValue, isResetGraph, scenarioResetValues);
        }
        const importedData = importGraphJsonFile(parameterValue);
        return {
          cells: importedData.cells,
          tables: importedData.tables,
          modules: importedData.modules,
        };
      }

      // Otherwise show the sample graph without setting it as a value
      return processDefaultGraph();
    } catch (error) {
      console.error('Error processing initial graph:', error);
      return { cells: [] };
    }
  }, [parameterValue, scenarioResetValues]);
};
